// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Alert, Button, Group, Loader, LoadingOverlay, Modal, Paper, Stack, Stepper } from '@mantine/core';
import { normalizeErrorString } from '@medplum/core';
import type {
  Coding,
  Encounter,
  Patient,
  Practitioner,
  Questionnaire,
  QuestionnaireResponse,
} from '@medplum/fhirtypes';
import { QuestionnaireForm, useMedplum, useMedplumProfile } from '@medplum/react';
import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { type IntakePhase, submitNpIntakeStep } from '../../services/np-intake-submit';
import { SleepHistoryIntakeForm } from './SleepHistoryIntakeForm';

const TAG_SYSTEM = 'http://polarissleep.com/tags';
const BOOKED_CODE = 'eligibility-appointment-booked';
const PENDING_CODE = 'eligibility-appointment-pending';
const PAGE_1_CODE = 'eligibility-check-page-1';
const SLEEP_HISTORY_CODE = 'sleep-history';
const FINISHED_CODE = 'eligibility-check-finished';

const STEPS = [
  { phase: 'eligibility-check-page-1' as const, label: 'Eligibility 1' },
  { phase: 'sleep-history' as const, label: 'Sleep history' },
];

interface IntakeScreenerModalProps {
  patient: Patient;
  encounter?: Encounter;
  opened: boolean;
  onClose: () => void;
  onCompleted?: () => void;
}

function getActiveStep(patient: Patient): number {
  const codes = patient.meta?.tag?.filter((tag) => tag.system === TAG_SYSTEM).map((tag) => tag.code) ?? [];
  if (codes.includes(FINISHED_CODE)) {
    return STEPS.length;
  }
  if (codes.includes(PAGE_1_CODE)) {
    return 0;
  }
  if (codes.includes(SLEEP_HISTORY_CODE) || codes.includes('stop-bang') || codes.includes('epworth-sleepiness-scale')) {
    return 1;
  }
  return 0;
}

export function IntakeScreenerModal({
  patient: initialPatient,
  encounter,
  opened,
  onClose,
  onCompleted,
}: IntakeScreenerModalProps): JSX.Element {
  const medplum = useMedplum();
  const profile = useMedplumProfile() as Practitioner | undefined;
  const [patient, setPatient] = useState(initialPatient);
  const [activeEncounter, setActiveEncounter] = useState(encounter);
  const [questionnaires, setQuestionnaires] = useState<Record<string, Questionnaire>>({});
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string>();
  const submitLock = useRef(false);

  const activeStep = useMemo(() => getActiveStep(patient), [patient]);
  const currentStep = STEPS[activeStep];

  useEffect(() => {
    if (!opened || !patient.id) {
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(undefined);
    Promise.all([
      medplum.readResource('Patient', patient.id),
      medplum.searchResources('Questionnaire', { name: 'eligibility-check-page-1,stop-bang,epworth-sleepiness-scale' }),
      Promise.resolve(encounter),
    ])
      .then(([loadedPatient, loadedQuestionnaires, loadedEncounter]) => {
        if (cancelled) {
          return;
        }
        const byName: Record<string, Questionnaire> = {};
        for (const questionnaire of loadedQuestionnaires) {
          if (questionnaire.name) {
            byName[questionnaire.name] = questionnaire;
          }
        }
        setPatient(loadedPatient);
        setQuestionnaires(byName);
        setActiveEncounter(loadedEncounter);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(normalizeErrorString(err));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [encounter, medplum, opened, patient.id]);

  const updatePatientPhase = async (phase: IntakePhase, completed: boolean): Promise<Patient> => {
    const nextCode = completed ? FINISHED_CODE : SLEEP_HISTORY_CODE;
    const nextPatient = await medplum.readResource('Patient', patient.id ?? '');
    const tags: Coding[] = nextPatient.meta?.tag ?? [];
    const progressCodes = new Set([
      BOOKED_CODE,
      PENDING_CODE,
      PAGE_1_CODE,
      SLEEP_HISTORY_CODE,
      FINISHED_CODE,
      'stop-bang',
      'epworth-sleepiness-scale',
    ]);
    const nextTags = tags.filter((tag: Coding) => !(tag.system === TAG_SYSTEM && progressCodes.has(tag.code ?? '')));
    nextTags.push({ system: TAG_SYSTEM, code: nextCode });
    return medplum.updateResource({ ...nextPatient, meta: { ...nextPatient.meta, tag: nextTags } });
  };

  const handleSubmit = async (
    response: QuestionnaireResponse,
    epworthResponse?: QuestionnaireResponse,
    stopBangResponse?: QuestionnaireResponse
  ): Promise<void> => {
    if (submitLock.current) {
      return;
    }
    if (!patient.id || !currentStep) {
      return;
    }
    submitLock.current = true;
    try {
      setSubmitting(true);
      if (activeEncounter?.id) {
        if (!profile) {
          setError('Your practitioner profile is still loading. Please try submitting again.');
          return;
        }
        await submitNpIntakeStep({
          medplum,
          patient,
          encounter: activeEncounter,
          practitioner: profile,
          phase: currentStep.phase,
          response,
          isFirstStep:
            patient.meta?.tag?.some(
              (tag) => tag.system === TAG_SYSTEM && (tag.code === BOOKED_CODE || tag.code === PENDING_CODE)
            ) ?? false,
          epworthResponse,
          stopBangResponse,
        });
      } else {
        const responses = [response, epworthResponse, stopBangResponse].filter(Boolean) as QuestionnaireResponse[];
        for (const item of responses) {
          let questionnaire: Questionnaire | undefined;
          if (item === response) {
            questionnaire = questionnaires[currentStep.phase];
          } else if (item === epworthResponse) {
            questionnaire = questionnaires['epworth-sleepiness-scale'];
          } else {
            questionnaire = questionnaires['stop-bang'];
          }
          await medplum.createResource({
            ...item,
            subject: { reference: `Patient/${patient.id}` },
            author: profile?.id ? { reference: `Practitioner/${profile.id}` } : undefined,
            questionnaire: questionnaire?.id ? `Questionnaire/${questionnaire.id}` : undefined,
          });
        }
        await updatePatientPhase(currentStep.phase, activeStep >= STEPS.length - 1);
      }

      const refreshed = await medplum.readResource('Patient', patient.id, { cache: 'no-cache' });
      setPatient(refreshed);
      if (activeStep >= STEPS.length - 1) {
        onCompleted?.();
        onClose();
      }
    } catch (err) {
      setError(normalizeErrorString(err));
    } finally {
      submitLock.current = false;
      setSubmitting(false);
    }
  };

  let body: JSX.Element;
  if (loading) {
    body = (
      <Stack align="center" py="xl">
        <Loader />
      </Stack>
    );
  } else if (error) {
    body = (
      <Alert color="red" title="Could not submit intake screener">
        {error}
      </Alert>
    );
  } else if (activeStep >= STEPS.length) {
    body = (
      <Alert color="green" title="Intake already completed">
        This patient’s intake screener is already complete.
      </Alert>
    );
  } else {
    const step = STEPS[activeStep];
    const questionnaire = questionnaires[step.phase];
    let formBody: JSX.Element;
    if (step.phase === SLEEP_HISTORY_CODE) {
      formBody = (
        <SleepHistoryIntakeForm
          submitting={submitting}
          onSubmit={(epworth, stopBang) =>
            handleSubmit({ resourceType: 'QuestionnaireResponse', status: 'completed' }, epworth, stopBang)
          }
        />
      );
    } else if (questionnaire) {
      formBody = (
        <QuestionnaireForm key={step.phase} questionnaire={questionnaire} onSubmit={handleSubmit} disablePagination />
      );
    } else {
      formBody = <Alert color="red">Questionnaire not found.</Alert>;
    }
    body = (
      <Stack gap="md">
        <Stepper active={activeStep} allowNextStepsSelect={false}>
          {STEPS.map((item) => (
            <Stepper.Step key={item.phase} label={item.label} />
          ))}
        </Stepper>
        <Paper withBorder p="md" pos="relative">
          <LoadingOverlay visible={submitting} />
          {formBody}
        </Paper>
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
        </Group>
      </Stack>
    );
  }

  return (
    <Modal opened={opened} onClose={onClose} title="Intake Screener" size="xl" centered>
      {body}
    </Modal>
  );
}
